import { Component, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  isChunkReload: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, isChunkReload: false };
  }

  static getDerivedStateFromError(error: Error): State {
    const msg = String(error?.message || "");
    const isChunkError =
      msg.includes("dynamically imported module") ||
      msg.includes("Failed to fetch dynamically imported") ||
      msg.includes("Importing a module script failed") ||
      /ChunkLoadError/i.test(msg);
    return { hasError: true, error, isChunkReload: isChunkError };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("ErrorBoundary caught:", error, info.componentStack);

    if (this.state.isChunkReload && typeof window !== "undefined") {
      const KEY = "__chunk_reload_attempted__";
      if (!sessionStorage.getItem(KEY)) {
        sessionStorage.setItem(KEY, "1");
        window.location.reload();
      }
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, isChunkReload: false });
  };

  render() {
    if (this.state.hasError && this.state.isChunkReload) {
      // Stale chunk after redeploy — reload is already in flight.
      // Show a neutral loading state instead of the scary error UI.
      return (
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      );
    }
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-6">
          <div className="max-w-md w-full text-center space-y-6">
            <AlertTriangle className="mx-auto h-12 w-12 text-destructive" />
            <h1 className="font-display text-3xl text-foreground">
              Něco se pokazilo
            </h1>
            <p className="text-muted-foreground text-sm">
              Omlouváme se, došlo k neočekávané chybě. Zkuste obnovit stránku.
            </p>
            <div className="flex gap-3 justify-center">
              <Button onClick={() => window.location.reload()}>
                Obnovit stránku
              </Button>
              <Button variant="outline" onClick={this.handleReset}>
                Zkusit znovu
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
